import { javaIsBlank, javaParseInt, javaStrip } from '../javaText.ts';

/**
 * Character sheets (docs/PARITY-STEP-2-PLAN.md section 5), as the desktop's `domain.Sheet*`,
 * `CharacterSheet` and `Condition` records: each one tidied on the way in exactly as its
 * Java constructor tidies it, so a template or sheet reads and writes the same in both
 * editions (schema/fixtures/sheet-cases.json).
 */

/** `SheetFieldType`, by name as the JSON writes it (in lower case). */
export type SheetFieldType = 'NUMBER' | 'TEXT' | 'LONG_TEXT' | 'CHECKBOX' | 'CHOICE' | 'RESOURCE' | 'DERIVED' | 'DICE';

/** Each type's name on screen, in the desktop's order. */
export const FIELD_TYPES: Readonly<Record<SheetFieldType, string>> = {
  NUMBER: 'Number',
  TEXT: 'Text',
  LONG_TEXT: 'Long text',
  CHECKBOX: 'Checkbox',
  CHOICE: 'Choice',
  RESOURCE: 'Resource',
  DERIVED: 'Derived',
  DICE: 'Dice roll',
};

/** Whether `@key` has a number to read. */
export const isNumericType = (type: SheetFieldType) => type !== 'TEXT' && type !== 'LONG_TEXT' && type !== 'DICE';

/** `SheetRole`, in its declaration order - which is the order roles are written and checked in. */
export type SheetRole = 'INITIATIVE' | 'HIT_POINTS' | 'DEFENSE';
export const SHEET_ROLES: readonly SheetRole[] = ['INITIATIVE', 'HIT_POINTS', 'DEFENSE'];
export const ROLE_NAMES: Readonly<Record<SheetRole, string>> = { INITIATIVE: 'Initiative', HIT_POINTS: 'Hit points', DEFENSE: 'Defense' };

export type SheetRoles = Readonly<Partial<Record<SheetRole, string>>>;

export interface SheetField {
  readonly key: string;
  readonly label: string;
  readonly type: SheetFieldType;
  /** What a new sheet starts with: for a resource, its starting maximum, or (with a formula) its starting value. */
  readonly defaultValue: string;
  /** A derived field's calculation, or a resource's maximum. */
  readonly formula: string;
  /** What the field's 🎲 rolls (`1d20 + @athletics`); a dice field rolls its own value instead. */
  readonly roll: string;
  /** A choice's options (`Trained=2`). */
  readonly options: readonly string[];
  readonly help: string;
}

/** `new SheetField(...)`: keys, formulas, rolls and help stripped; a blank label is the key. */
export function sheetField(
  key: string | null,
  label: string | null,
  type: SheetFieldType | null,
  defaultValue: string | null = '',
  formula: string | null = '',
  roll: string | null = '',
  options: readonly string[] | null = [],
  help: string | null = '',
): SheetField {
  const strippedKey = javaStrip(key ?? '');
  return {
    key: strippedKey,
    label: label === null || javaIsBlank(label) ? strippedKey : javaStrip(label),
    type: type ?? 'TEXT',
    defaultValue: defaultValue ?? '',
    formula: javaStrip(formula ?? ''),
    roll: javaStrip(roll ?? ''),
    options: [...(options ?? [])],
    help: javaStrip(help ?? ''),
  };
}

/** Whether the field has a roll button. */
export const isRollable = (field: SheetField) => field.type === 'DICE' || field.roll !== '';

/** `SheetField.keyFor`: "Sleight of Hand" -> "sleight_of_hand". */
export function keyFor(label: string | null): string {
  const key = (label ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  if (key === '') return 'field';
  return /^[0-9]/.test(key) ? `f_${key}` : key;
}

export interface SheetSection {
  readonly title: string;
  /** 1 to 4. */
  readonly columns: number;
  readonly fields: readonly SheetField[];
}

export function sheetSection(title: string | null, columns: number, fields: readonly SheetField[] | null): SheetSection {
  return { title: javaStrip(title ?? ''), columns: Math.max(1, Math.min(4, columns)), fields: [...(fields ?? [])] };
}

export interface SheetTemplate {
  readonly id: string;
  readonly name: string;
  readonly system: string;
  readonly description: string;
  /** The attribution the game's licence requires, shown on every sheet. */
  readonly license: string;
  /** Which built-in this is the realm's copy of; blank for the realm's own. */
  readonly builtinKey: string;
  readonly sections: readonly SheetSection[];
  readonly roles: SheetRoles;
  /** The conditions offered for its characters (Prone, Frightened...). */
  readonly conditions: readonly string[];
  readonly created: string;
  readonly modified: string;
}

export interface TemplateParts {
  readonly name: string | null;
  readonly system: string | null;
  readonly description: string | null;
  readonly license: string | null;
  readonly builtinKey: string | null;
  readonly sections: readonly SheetSection[] | null;
  readonly roles: SheetRoles | null;
  readonly conditions?: readonly string[] | null;
}

/** `new SheetTemplate(...)`: "Untitled Template" for a blank name; roles without a key, and blank or repeated conditions, dropped. */
export function sheetTemplate(id: string, parts: TemplateParts, created: string, modified: string): SheetTemplate {
  const roles: Partial<Record<SheetRole, string>> = {};
  for (const role of SHEET_ROLES) {
    const key = parts.roles?.[role];
    if (key !== undefined && !javaIsBlank(key)) roles[role] = javaStrip(key);
  }
  return {
    id,
    name: parts.name === null || javaIsBlank(parts.name) ? 'Untitled Template' : javaStrip(parts.name),
    system: javaStrip(parts.system ?? ''),
    description: javaStrip(parts.description ?? ''),
    license: javaStrip(parts.license ?? ''),
    builtinKey: javaStrip(parts.builtinKey ?? ''),
    sections: [...(parts.sections ?? [])],
    roles,
    conditions: [...new Set((parts.conditions ?? []).map(javaStrip).filter((c) => c !== ''))],
    created,
    modified,
  };
}

export const isBuiltIn = (template: SheetTemplate) => template.builtinKey !== '';

/** Every field, in section order. */
export const allFields = (template: SheetTemplate): SheetField[] => template.sections.flatMap((section) => section.fields);

export const templateField = (template: SheetTemplate, key: string): SheetField | null => allFields(template).find((f) => f.key === key) ?? null;

export function fieldFor(template: SheetTemplate, role: SheetRole): SheetField | null {
  const key = template.roles[role];
  return key === undefined ? null : templateField(template, key);
}

/** Field by field, as Java's record `equals` compares two templates' sections. */
export function sameSections(a: readonly SheetSection[], b: readonly SheetSection[]): boolean {
  const field = (x: SheetField, y: SheetField) =>
    x.key === y.key &&
    x.label === y.label &&
    x.type === y.type &&
    x.defaultValue === y.defaultValue &&
    x.formula === y.formula &&
    x.roll === y.roll &&
    x.help === y.help &&
    x.options.length === y.options.length &&
    x.options.every((o, i) => o === y.options[i]);
  return (
    a.length === b.length &&
    a.every((s, i) => {
      const t = b[i]!;
      return s.title === t.title && s.columns === t.columns && s.fields.length === t.fields.length && s.fields.every((f, j) => field(f, t.fields[j]!));
    })
  );
}

/**
 * Where a sheet keeps its conditions, in {@link encodeConditions} form; field keys start
 * with a letter, so this never collides with one.
 */
export const CONDITIONS_KEY = '_conditions';

export interface CharacterSheet {
  readonly id: string;
  readonly templateId: string;
  /** The lore entry it belongs to; an entry has at most one sheet. */
  readonly entryId: string | null;
  readonly name: string;
  /** What's written in each field, by key; a resource's entered maximum is under `key.max`. Unwritten fields use the template's defaults. */
  readonly values: ReadonlyMap<string, string>;
  readonly created: string;
  readonly modified: string;
}

/** `new CharacterSheet(...)`: "Unnamed Character" for a blank name; a blank entry is none. */
export function characterSheet(
  id: string,
  templateId: string,
  entryId: string | null,
  name: string | null,
  values: ReadonlyMap<string, string> | null,
  created: string,
  modified: string,
): CharacterSheet {
  return {
    id,
    templateId,
    entryId: entryId === null || javaIsBlank(entryId) ? null : entryId,
    name: name === null || javaIsBlank(name) ? 'Unnamed Character' : javaStrip(name),
    values: new Map(values ?? []),
    created,
    modified,
  };
}

/** `withValue`: one field changed; an empty value clears it back to the template's default. */
export function withValue(values: ReadonlyMap<string, string>, key: string, value: string | null): Map<string, string> {
  const updated = new Map(values);
  if (value === null || value === '') updated.delete(key);
  else updated.set(key, value);
  return updated;
}

/** `Condition`: "Prone", or "Frightened" with rounds left; 0 lasts until removed. */
export interface Condition {
  readonly name: string;
  readonly roundsLeft: number;
}

export function condition(name: string | null, roundsLeft: number): Condition {
  return { name: javaStrip((name ?? '').replaceAll(';', ',').replaceAll(':', ' ')), roundsLeft: Math.max(0, roundsLeft) };
}

/** "Frightened (2 rounds)", or just "Prone". */
export const conditionLabel = (c: Condition) => (c.roundsLeft === 0 ? c.name : `${c.name} (${c.roundsLeft}${c.roundsLeft === 1 ? ' round)' : ' rounds)'}`);

/** "Prone;Frightened:2". */
export function encodeConditions(conditions: readonly Condition[]): string {
  return conditions
    .filter((c) => c.name !== '')
    .map((c) => (c.roundsLeft > 0 ? `${c.name}:${c.roundsLeft}` : c.name))
    .join(';');
}

export function decodeConditions(text: string | null | undefined): Condition[] {
  if (text === null || text === undefined || javaIsBlank(text)) return [];
  const conditions: Condition[] = [];
  for (const part of text.split(';')) {
    const trimmed = javaStrip(part);
    if (trimmed === '') continue;
    const colon = trimmed.lastIndexOf(':');
    let rounds = 0;
    let name = trimmed;
    if (colon > 0) {
      const parsed = javaParseInt(javaStrip(trimmed.slice(colon + 1)));
      // Otherwise not a round count: the colon is part of the name.
      if (parsed !== null) {
        rounds = parsed;
        name = trimmed.slice(0, colon);
      }
    }
    conditions.push(condition(name, rounds));
  }
  return conditions;
}
